import { useMemo } from 'react';

import { ApiUrl } from '@common/enums';
import { SectionTypes } from '@common/enums/section-type-enum';
import { ApplianceModel, ApplianceSource } from '@common/models';
import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/dist/query';

import {
    useFetchInStoreRefrigeratorsQuery,
    useFetchProgramRefrigeratorsQuery,
} from '../../../../../api/static-content-api/refrigerators-api/refrigeratorsService';
import { makeRefrigeratorsSelector } from './refrigerator.utils';

interface useRefrigeratorReturn {
    browsedRefrigerators: ApplianceModel[];
    isFetchingRefrigerators?: boolean;
    isErrorRefrigerators?: boolean;
}

const emptyArray = [] satisfies ApplianceModel[];

// eslint-disable-next-line sonarjs/cognitive-complexity
const useRefrigerator = (): useRefrigeratorReturn => {
    const { isMaterialLoaded } = useMaterials();

    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { isFetchingDependencies } = useFetchDependencies();

    const zipcode = useAppSelector(selectZipcode);

    const selectInStoreRefrigeratorsTransformed = useMemo(() => makeRefrigeratorsSelector(), []);

    const inStoreRefrigeratorsUrl = useRetailerSettings().getInStoreApplianceUrl(SectionTypes.refrigerator);

    const {
        inStoreRefrigerators,
        isFetching: isFetchingInStoreRefrigerators,
        error: isErrorInStoreRefrigerators,
    } = useFetchInStoreRefrigeratorsQuery(
        !inStoreRefrigeratorsUrl ||
            isFetchingDependencies ||
            !zipcode ||
            !selectedColor ||
            !selectedStore ||
            !isMaterialLoaded
            ? skipToken
            : {
                  url: inStoreRefrigeratorsUrl,
              },
        {
            selectFromResult: ({ data, ...rest }) => ({
                inStoreRefrigerators:
                    data && !rest.isError
                        ? selectInStoreRefrigeratorsTransformed(
                              data ?? emptyArray,
                              ApplianceSource.IN_STORE,
                              undefined,
                              true
                          )
                        : emptyArray,
                ...rest,
            }),
        }
    );

    const {
        programRefrigerators,
        isFetching: isFetchingProgramRefrigerators,
        isError: isErrorProgramRefrigerators,
    } = useFetchProgramRefrigeratorsQuery(
        isFetchingDependencies ||
            !ApiUrl.REFRIGERATORS ||
            !zipcode ||
            !selectedColor ||
            !selectedStore ||
            !isMaterialLoaded
            ? skipToken
            : {
                  params: {
                      zipCode: zipcode,
                      storeId: selectedStore?.companyId,
                      ...(selectedColor.qteGrpID ? { prodQteGrpID: selectedColor.qteGrpID } : {}),
                  },
              },
        {
            selectFromResult: ({ data, ...rest }) => ({
                programRefrigerators:
                    data && !rest.isError
                        ? selectInStoreRefrigeratorsTransformed(
                              data ?? emptyArray,
                              ApplianceSource.PROGRAM,
                              undefined,
                              true
                          )
                        : emptyArray,
                ...rest,
            }),
        }
    );

    const browsedRefrigerators = useMemo(
        () => [...programRefrigerators, ...inStoreRefrigerators],
        [programRefrigerators, inStoreRefrigerators]
    );

    return {
        browsedRefrigerators,
        isFetchingRefrigerators: isFetchingProgramRefrigerators || isFetchingInStoreRefrigerators,
        isErrorRefrigerators: !!isErrorInStoreRefrigerators || !!isErrorProgramRefrigerators,
    };
};

export default useRefrigerator;
